<template>
  <div class="w-full">
    <SharedPolkaBtn
      v-if="!getLotById.quantity"
      id="getorder"
      field="addToCart"
      color="primary"
      size="large"
      class="w-full mt-4"
      @click="handleClickAddToCart"
    >
      {{ equipmentAndVehicles ? 'Арендовать' : 'Добавить в корзину' }}
    </SharedPolkaBtn>
    <div
      v-else
      class="flex justify-between gap-x-4 mt-4 relative"
    >
      <PolkaStepper
        size="large"
        :quantity="getLotById.quantity"
        :plus-disabled="getLotById.quantity >= lot.quantity"
        :minus-disabled="getLotById.quantity === 1"
        @minus="handleMinusFromCart"
        @plus="handlePlusToCart"
      />

      <SharedPolkaBtn
        color="secondary"
        size="large"
        class="w-full"
        @click="goToCart"
      >
        В корзину
      </SharedPolkaBtn>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick } from 'vue'
import PolkaStepper from '~/components/shared/polka_stepper.vue'
import { useLot } from '~/composables/useLot'

const props = defineProps({
  lotId: {
    type: [String, Number],
    required: true,
  },
  insuranceSelected: {
    type: Boolean,
    default: true,
  },
})

// Composables
const { $router, $modal, $tracking } = useNuxtApp()
const localePath = useLocalePath()

// Store data
const isAdultConfirmed = useGlobal().isAdultConfirmed
const cart = useCart().item
const { lot } = useLot(props.lotId)
const priceDetails = computed(() => lot.value.priceDetails)

// Store actions
const cartAddLot = useCart().cartAddLot
const cartUpdateDates = useCart().cartUpdateDates
const cartUpdateLotQuantity = useCart().cartUpdateLotQuantity
const cartRemoveLot = useCart().cartRemoveLot

// Computed
const getLotById = computed(() => {
  return useCart().getLotById(lot.value.id)
})

const hasSelectedDate = computed(() => {
  return cart.value.dateFrom && cart.value.dateTo
})

const equipmentAndVehicles = computed(() => {
  return Boolean(lot.value?.category?.equipmentAndVehicles)
})

const needBlur = computed(() => {
  return lot.value.adultOnly && !isAdultConfirmed.value
})

const wantInsuranceOnCartAdd = () => {
  return Boolean(lot.value?.category?.insurance) && props.insuranceSelected === true
}

// Methods
const goToCart = () => {
  $router.push(localePath('/cart'))
}

const handleMinusFromCart = async () => {
  useStoreFields().startSubmitting('addToCart')

  if (getLotById.value.quantity === 1) {
    await cartRemoveLot({ lotId: lot.value.id })
  } else {
    await cartUpdateLotQuantity({
      lotId: lot.value.id,
      quantity: getLotById.value.quantity - 1,
    })
  }
  useStoreFields().finishSubmitting('addToCart')
}

const handleClickAddToCart = () => {
  if (needBlur.value) {
    $tracking.logEventAmplitude('show confirmation', {
      'confirmation type': 'AGE',
    })
    $modal.show('shared/modal/adult_confirmation', {
      minWidthClass: 'sm:min-w-[328px]',
      centered: true,
      confirmationMethod: () => {
        const rmAdultConfirmedCookie = useAppCookie('rm_adult_confirmed')
        rmAdultConfirmedCookie.value = true
        useGlobal().setIsAdultConfirmed(true)
        nextTick(() => {
          handleAddToCart()
        })
      },
    })
  } else {
    handleAddToCart()
  }
}

const handleAddToCart = async () => {
  if (!hasSelectedDate.value) {
    handleSelectDate()
    return
  }
  if (getLotById.value.quantity) {
    await handlePlusToCart()
    return
  }
  useStoreFields().startSubmitting('addToCart')
  try {
    await cartAddLot({
      lotId: lot.value.id,
      insurance: wantInsuranceOnCartAdd(),
    })
  } finally {
    useStoreFields().finishSubmitting('addToCart')
  }
}

const handlePlusToCart = async () => {
  useStoreFields().startSubmitting('addToCart')
  await cartUpdateLotQuantity({
    lotId: lot.value.id,
    quantity: getLotById.value.quantity + 1,
  })
  useStoreFields().finishSubmitting('addToCart')
}

const handleSelectDate = () => {
  $modal.show('lots/show/date_select', {
    noPadding: true,
    minRentDays: lot.value.minRentDays,
    dates: {
      start: cart.value.dateFrom,
      end: cart.value.dateTo,
    },
    id: lot.value.id,
    confirmationMethod: confirmationDate,
    hourlyRentPrices: lot.value.hourlyRentPrices,
    hourlyRentAvailable: lot.value.prices?.hourlyRentAvailable,
    prices: lot.value.prices,
    isHourlyRent: cart.value.isHourlyRent,
    cartRentHours: cart.value.rentHours || 1,
    priceDetails: priceDetails.value,
    hasInCart: getLotById.value.quantity > 0,
    equipmentAndVehicles: equipmentAndVehicles.value,
  })
}

const confirmationDate = async (range, rentHours) => {
  useStoreFields().startSubmitting('addToCart')
  await cartUpdateDates({
    dateFrom: range.start,
    dateTo: range.end,
    source: 'lot',
    lotId: lot.value.id,
    lot: lot.value,
    rentHours,
  })
  const willAdd = !getLotById.value.quantity
  if (willAdd) {
    await cartAddLot({
      lotId: lot.value.id,
      analytics: {},
      insurance: wantInsuranceOnCartAdd(),
    })
  }
  useStoreFields().finishSubmitting('addToCart')
}
</script>

<style lang="scss" scoped></style>
